import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { tickStore, type TickLike } from '../renderer/tick-store.js';
import { getSymbolLabel } from '../state/symbol-labels.js';
import { formatPrice } from '../state/format.js';
import { connectionState } from '../state/connection-state.js';

interface RowState {
    symbol: string;
    tick?: TickLike;
    direction: 'up' | 'down' | 'flat';
    flash: '' | 'up' | 'down';
    prevBid?: string;
}

/**
 * Fiyat Listesi — yatay satırlar (isim solda, ALIŞ/SATIŞ sağda). 1/2/3 sütun
 * (list.css gazete akışı). Fiyat değişiminde kısa flash + yön oku. Satır yazıları
 * container-query ile satır yüksekliğine ölçeklenir.
 */
@customElement('fp-symbol-list')
export class FpSymbolList extends LitElement {
    @property({ type: Number }) columns = 1;
    @property({ type: Array }) symbols: string[] = [];
    @property({ type: Array }) show: string[] = ['bid', 'ask', 'direction'];
    /** Sütun başlığı satırı (ÜRÜN / ALIŞ / SATIŞ) + hizalı sütunlar (tablo görünümü). */
    @property({ type: Boolean }) showHeader = false;
    /** Yazı boyutu çarpanı: auto=1 / large=1.25 / xlarge=1.5 (--fp-list-scale). */
    @property({ type: String }) textSize: 'auto' | 'large' | 'xlarge' = 'auto';
    /**
     * Sütun başına sembol grupları. Doluysa semboller otomatik dağıtılmaz; her dış
     * eleman bir sütundur (yukarıdan aşağı). Boşsa `symbols` `columns` sütununa
     * gazete akışıyla otomatik bölünür (geri uyumlu).
     */
    @property({ type: Array }) columnSymbols: string[][] = [];

    @state() private rows: RowState[] = [];
    private unsubscribe?: () => void;
    private flashTimers = new Map<string, number>();
    private lastEpoch = connectionState.reconnectEpoch;

    createRenderRoot(): HTMLElement {
        return this;
    }

    /** Abonelik/satır kümesi: sütun grupları varsa onların düz birleşimi, yoksa `symbols`. */
    private effectiveSymbols(): string[] {
        const grouped = this.columnSymbols.flat();
        return grouped.length > 0 ? grouped : this.symbols;
    }

    connectedCallback(): void {
        super.connectedCallback();
        this.rows = this.effectiveSymbols().map((symbol) => ({
            symbol,
            tick: tickStore.get(symbol),
            direction: 'flat',
            flash: '',
            prevBid: tickStore.get(symbol)?.bid,
        }));
        this.unsubscribe = tickStore.subscribe((sym, tick) => {
            const idx = this.rows.findIndex((r) => r.symbol === sym);
            if (idx === -1) return;
            const row = this.rows[idx]!;
            let direction: RowState['direction'] = row.direction;
            let flash: RowState['flash'] = row.flash;
            // Reconnect sonrası ilk tick'te sahte yön/flash bastır (O5).
            const epoch = connectionState.reconnectEpoch;
            const suppress = epoch !== this.lastEpoch;
            if (suppress) this.lastEpoch = epoch;
            if (!suppress && row.prevBid !== undefined && tick.bid !== undefined && tick.bid !== row.prevBid) {
                const a = parseFloat(tick.bid);
                const b = parseFloat(row.prevBid);
                if (Number.isFinite(a) && Number.isFinite(b) && a !== b) {
                    direction = a > b ? 'up' : 'down';
                    flash = direction;
                    this.scheduleFlashClear(sym);
                }
            }
            this.rows = [
                ...this.rows.slice(0, idx),
                { ...row, tick, direction, flash, prevBid: tick.bid ?? row.prevBid },
                ...this.rows.slice(idx + 1),
            ];
        });
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.unsubscribe?.();
        this.unsubscribe = undefined;
        for (const t of this.flashTimers.values()) clearTimeout(t);
        this.flashTimers.clear();
    }

    /** 0.7s sonra ilgili satırın flash'ını temizle. */
    private scheduleFlashClear(symbol: string): void {
        const existing = this.flashTimers.get(symbol);
        if (existing !== undefined) clearTimeout(existing);
        this.flashTimers.set(
            symbol,
            window.setTimeout(() => {
                const idx = this.rows.findIndex((r) => r.symbol === symbol);
                if (idx === -1) return;
                this.rows = [
                    ...this.rows.slice(0, idx),
                    { ...this.rows[idx]!, flash: '' },
                    ...this.rows.slice(idx + 1),
                ];
                this.flashTimers.delete(symbol);
            }, 700),
        );
    }

    private valCell(label: string, value: string, arrow: unknown): unknown {
        return html`
            <span class="fp-list-cell">
                <span class="fp-list-lbl">${label}</span>
                <span class="fp-list-val">${value}${arrow}</span>
            </span>
        `;
    }

    /** Tek bir fiyat satırını çiz. */
    private renderRow(r: RowState, showBid: boolean, showAsk: boolean, showDir: boolean): unknown {
        const arrow = r.direction === 'up' ? '▲' : r.direction === 'down' ? '▼' : '';
        const arrowCls =
            r.direction === 'up' ? 'fp-list-arrow--up'
            : r.direction === 'down' ? 'fp-list-arrow--down'
            : '';
        const arrowTpl =
            showDir && arrow
                ? html`<span class="fp-list-arrow ${arrowCls}">${arrow}</span>`
                : '';
        const flashCls = r.flash === 'up' ? 'fp-flash-up' : r.flash === 'down' ? 'fp-flash-down' : '';
        return html`
            <div class="fp-list-row ${flashCls}">
                <span class="fp-list-name" title="${getSymbolLabel(r.symbol) ?? r.symbol}"
                    >${getSymbolLabel(r.symbol) ?? r.symbol}</span
                >
                <div class="fp-list-vals">
                    ${showBid ? this.valCell('ALIŞ', formatPrice(r.tick?.bid), arrowTpl) : ''}
                    ${showAsk ? this.valCell('SATIŞ', formatPrice(r.tick?.ask), '') : ''}
                </div>
            </div>
        `;
    }

    /** Satırları sütunlara ayır: column_symbols varsa onları, yoksa gazete akışıyla eşit parça. */
    private columnGroups(cols: number): RowState[][] {
        const explicit = this.columnSymbols.slice(0, 3).filter((g) => g.length > 0);
        if (explicit.length > 0) {
            const rowOf = new Map(this.rows.map((r) => [r.symbol, r]));
            return explicit.map((g) => g.map((s) => rowOf.get(s)).filter((r): r is RowState => !!r));
        }
        const per = Math.max(1, Math.ceil(this.rows.length / cols));
        const out: RowState[][] = [];
        for (let i = 0; i < cols; i++) out.push(this.rows.slice(i * per, (i + 1) * per));
        return out.filter((g) => g.length > 0);
    }

    /** Tablo modu satırı: ÜRÜN | ALIŞ | SATIŞ hizalı grid (etiketsiz). */
    private renderTableRow(r: RowState, showBid: boolean, showAsk: boolean, showDir: boolean): unknown {
        const arrow = r.direction === 'up' ? '▲' : r.direction === 'down' ? '▼' : '';
        const arrowCls =
            r.direction === 'up' ? 'fp-list-arrow--up'
            : r.direction === 'down' ? 'fp-list-arrow--down'
            : '';
        const arrowTpl =
            showDir && arrow ? html`<span class="fp-list-arrow ${arrowCls}">${arrow}</span>` : '';
        const flashCls = r.flash === 'up' ? 'fp-flash-up' : r.flash === 'down' ? 'fp-flash-down' : '';
        const label = getSymbolLabel(r.symbol) ?? r.symbol;
        return html`
            <div class="fp-list-row fp-list-trow ${flashCls}">
                <span class="fp-list-name" title="${label}"
                    ><span class="fp-list-nametext">${label}</span>${arrowTpl}</span
                >
                ${showBid ? html`<span class="fp-list-val fp-list-tval">${formatPrice(r.tick?.bid)}</span>` : ''}
                ${showAsk ? html`<span class="fp-list-val fp-list-tval">${formatPrice(r.tick?.ask)}</span>` : ''}
            </div>
        `;
    }

    render() {
        // Yazı boyutu ölçeği — host'a yazılır, light DOM tüm satırlara miras geçer.
        const scale = this.textSize === 'large' ? 1.25 : this.textSize === 'xlarge' ? 1.5 : 1;
        this.style.setProperty('--fp-list-scale', String(scale));

        if (this.rows.length === 0) {
            return html`<div class="fp-empty">Bu ekran için henüz sembol seçilmedi.</div>`;
        }
        const showBid = this.show.includes('bid');
        const showAsk = this.show.includes('ask');
        const showDir = this.show.includes('direction');

        // Tablo modu (sütun başlıklı): her sütun = başlık satırı + hizalı veri satırları.
        if (this.showHeader) {
            const cols = Math.min(3, Math.max(1, this.columns || 1));
            const groups = this.columnGroups(cols);
            const valCols = (showBid ? 1 : 0) + (showAsk ? 1 : 0);
            const tcols = `minmax(0, 1.7fr)${' minmax(0, 1fr)'.repeat(valCols)}`;
            const wrapCols = Math.max(1, groups.length);
            return html`
                <div class="fp-list fp-list--table" style="grid-template-columns: repeat(${wrapCols}, minmax(0, 1fr));">
                    ${groups.map(
                        (col) => html`
                            <div class="fp-list-tcol" style="--fp-tcols: ${tcols};">
                                <div class="fp-list-row fp-list-head">
                                    <span class="fp-list-hname">ÜRÜN</span>
                                    ${showBid ? html`<span class="fp-list-hcell">ALIŞ</span>` : ''}
                                    ${showAsk ? html`<span class="fp-list-hcell">SATIŞ</span>` : ''}
                                </div>
                                ${col.map((r) => this.renderTableRow(r, showBid, showAsk, showDir))}
                            </div>
                        `,
                    )}
                </div>
            `;
        }

        // Sütun-bazlı (özel dağıtım): her grup ayrı sütun, satırlar sütun içinde eşit
        // yükseklik. Boş sütunlar KORUNUR (yer boş kalır) → kullanıcının sütun konumu kaymaz.
        const groups = this.columnSymbols.slice(0, 3);
        if (groups.some((g) => g.length > 0)) {
            const rowOf = new Map(this.rows.map((r) => [r.symbol, r]));
            const colStyle = `grid-template-columns: repeat(${groups.length}, minmax(0, 1fr));`;
            return html`
                <div class="fp-list fp-list--custom" style="${colStyle}">
                    ${groups.map(
                        (col) => html`
                            <div class="fp-list-col">
                                ${col.map((sym) => {
                                    const r = rowOf.get(sym);
                                    return r ? this.renderRow(r, showBid, showAsk, showDir) : '';
                                })}
                            </div>
                        `,
                    )}
                </div>
            `;
        }

        // Otomatik dağıtım (gazete akışı) — geri uyumlu.
        const cols = Math.min(3, Math.max(1, this.columns || 1));
        const rowsPerCol = Math.max(1, Math.ceil(this.rows.length / cols));
        const gridStyle = `grid-template-columns: repeat(${cols}, minmax(0, 1fr)); grid-template-rows: repeat(${rowsPerCol}, minmax(0, 1fr));`;
        return html`
            <div class="fp-list" style="${gridStyle}">
                ${this.rows.map((r) => this.renderRow(r, showBid, showAsk, showDir))}
            </div>
        `;
    }
}
